import { styled } from '@components-shared/src/stitches';
import { AuctionType } from '@graphql/reactQuery';
import type { SongDefaultRewards } from '@utils/constants';
import { COMMENT_REWARD_DESCRIPTION, REWARDS_TITLE, SONG_DEFAULT_REWARDS } from '@utils/constants';

export const Section = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  marginBottom: 32,
});

export const Header = styled('div', {
  width: 552,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  h3: {
    base: 'L',
    fontStyle: 'semibold',
  },
  p: {
    base: 'S',
    color: '$neutral500',
  },
});

export const OptionalTag = styled('div', {
  display: 'flex',
  backgroundColor: '$neutral200',
  color: '$neutral500',
  padding: '0px 4px',
  fontFamily: '$base',
  fontSize: 11,
  fontWeight: 500,
  height: 16,
  borderRadius: 20,
});

export const DropZoneContainer = styled('div', {
  width: '100%',
  maxWidth: 336,
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
});

export const DropZone = styled('div', {
  border: '1px solid $neutral300',
  color: '$neutral400',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  height: 160,
  width: 160,
  borderRadius: 6,
  flexShrink: 0,
  marginRight: 24,
  paddingX: 10,

  div: {
    fontSize: 12,
    lineHeight: '16px',
  },

  variants: {
    isDisabled: {
      true: {
        border: '1px solid $neutral200',
        cursor: 'not-allowed',
        color: '$neutral200',
      },
    },
  },
});

export const NEW_DRAFT_ID = 'new';

export const TooltipContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

export const TooltipHeader = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  title: 'S',
  color: '$white',
});

export const FormDetails = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  base: 'S',
  color: '$neutral400',
  lineHeight: '18px',
});

export const UPDATE_CONFIRMATION_DESCRIPTION_ONE = `You'll be prompted to sign one or multiple transactions in your wallet. You'll be responsible for the gas fees needed to make these updates to your contract.`;

export const UPDATE_CONFIRMATION_DESCRIPTION_TWO = `Any changes will cost additional gas so please verify that the above information is accurate.`;

export const RIGHTS_CONFIRMATION_COPY = `I confirm that I fully own the rights to this release or have the required clearance.`;

export const EDIT_GAS_CONFIRMATION_COPY = `I understand that any changes after upload will require additional gas fees.`;

export const REVIEW_AND_DEPLOY_EDUCATION_TEXT =
  'Carefully review your release info. After you upload, any changes will require additional gas fees.';

export const REVIEW_AND_DEPLOY_V2_EDUCATION_TEXT =
  'Carefully review the below details before uploading. Any edits to your song after you upload will require additional gas fees. Drop type and phases cannot be edited once started.';

export const SALES_TO_SOUND_COPY = '5% of primary sales will go to Sound.';

export const CURATOR_REWARDS_DESCRIPTION =
  'Build your own street team and incentivize your listeners to share your release! Collectors can share your song or add it to a playlist to earn when they refer someone else to collect your song.';

export const COLUMN_WIDTH = 111;
export const COLUMN_GAP = 35;

export const Content = styled('div', {
  base: 'M',
  width: 410,
  whiteSpace: 'pre-wrap',

  '@desktop': {
    base: 'L',
    fontStyle: 'regular',
  },

  variants: {
    color: {
      grey: {
        color: '$neutral500',
      },
    },
  },
});

export const SectionHeader = styled('h3', {
  display: 'flex',
  justifyContent: 'space-between',
  title: 'M',
  fontStyle: 'medium',
  alignItems: 'center',
});

export const SectionContainer = styled('div', {
  position: 'relative',
  width: 676,
  shadow: 'M',
  borderRadius: 8,
  padding: '24px 16px',
  marginBottom: 28,
  variants: {
    isSingle: {
      true: {
        width: 590,
        '@desktop': {
          width: 676,
        },
      },
    },
  },
});

export const SectionInfo = styled('div', {
  marginTop: 32,
});

export const Warning = styled('div', {
  fontSize: 14,
  fontFamily: '$base',
  fontWeight: 500,
  lineHeight: '18px',
  color: '#605DEC',
  marginTop: 16,
});

export const SeeMoreText = styled('div', {
  cursor: 'pointer',
  base: 'L',
  fontStyle: 'medium',
  color: '$neutral600',
  marginTop: 8,
});

export const Row = styled('div', {
  display: 'flex',
  gap: 32,
  borderBottom: '1px solid $neutral200',
  paddingY: 12,
  width: '100%',
  variants: {
    isLast: {
      true: {
        border: 'none',
      },
    },
  },
});

export const Label = styled('div', {
  base: 'L',
  fontWeight: 500,
  color: '$neutral400',
  width: 200,
});

export const Body = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: 34,
});

export const formattedAuctionType = ({ auctionType }: { auctionType: AuctionType }) => {
  if (auctionType === AuctionType.FixedQuantity) {
    return 'Fixed Editions';
  }
  if (auctionType === AuctionType.OpenEdition) {
    return 'Open Editions';
  }
  if (auctionType === AuctionType.OpenEditionWithSam) {
    return 'Sound Swap';
  }
  return '';
};

export type RewardType = {
  title: string;
  description: React.ReactNode | string;
};

export const HeaderRow = styled('div', {
  display: 'flex',
  marginTop: 24,
  paddingY: 12,
  borderBottom: '1px solid $neutral200',
  paddingLeft: 231,
});

export const RowTitle = styled('div', {
  base: 'L',
  color: '$neutral400',
  display: 'flex',
});

export const TableRow = styled('div', {
  display: 'flex',
  gap: 32,
  width: '100%',
  paddingY: 10,
  borderBottom: '1px solid $neutral200',
  variants: {
    isLast: {
      true: {
        borderBottom: 'none',
      },
    },
  },
});

export const SubRowContent = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
});

export const TableLabel = styled('div', {
  base: 'L',
  width: 200,
  fontWeight: 500,
  color: '$neutral400',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  span: {
    base: 'S',
    color: '$neutral400',
  },
});

export const TableContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: 111,
  base: 'M',
  button: {
    fontSize: 12,
    textDecoration: 'underline',
    cursor: 'pointer',
    textAlign: 'left',
  },
  '&:nth-child(1)': {
    marginRight: 35,
  },
  '&:nth-child(2)': {
    marginRight: 28,
  },

  '@desktop': {
    base: 'L',
  },
});

export const defaultRewardsInfo = ({ goldenEggContent }: { goldenEggContent: React.ReactNode }) => {
  const rewards: { [key in SongDefaultRewards]: RewardType } = {
    [SONG_DEFAULT_REWARDS.GOLDEN_EGG]: {
      title: `${REWARDS_TITLE[SONG_DEFAULT_REWARDS.GOLDEN_EGG]} Image`,
      description: goldenEggContent,
    },
    [SONG_DEFAULT_REWARDS.COMMENT]: {
      title: REWARDS_TITLE[SONG_DEFAULT_REWARDS.COMMENT],
      description: COMMENT_REWARD_DESCRIPTION,
    },
  } as const;

  return rewards;
};
